/**
 * ProactiveAgentPage - Dashboard for AI-driven alerts and pattern detection
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Alert, AlertDescription } from "../components/ui/alert";

import {
  Zap,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Clock,
  RefreshCw,
  Eye,
  Check,
  FileText,
  ArrowLeft,
  Loader2,
  Bell,
  TrendingUp,
  DollarSign,
  ClipboardCheck,
  MessageSquare,
  Calendar,
  Sparkles
} from "lucide-react";

const PRIORITY_CONFIG = {
  critical: { color: "bg-red-500", textColor: "text-red-600", icon: AlertCircle, label: "Critical" },
  high: { color: "bg-orange-500", textColor: "text-orange-600", icon: AlertTriangle, label: "High" },
  medium: { color: "bg-yellow-500", textColor: "text-yellow-600", icon: Clock, label: "Medium" },
  low: { color: "bg-blue-500", textColor: "text-blue-600", icon: Bell, label: "Low" }
};

const ALERT_TYPE_ICONS = {
  violation_trend: TrendingUp,
  assessment_delinquency: DollarSign,
  drc_spike: ClipboardCheck,
  common_questions: MessageSquare,
  meeting_reminder: Calendar,
  anomaly_detected: Zap
};

const ProactiveAgentPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("active");
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [generatedAlerts, setGeneratedAlerts] = useState(null);
  
  // Dialog state
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [draftDialog, setDraftDialog] = useState({ open: false, alert: null, draft: "" });
  const [draftLoading, setDraftLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [alertsRes, statsRes] = await Promise.all([
        axios.get(`${API}/proactive/alerts/active?include_acknowledged=true`, { headers }),
        axios.get(`${API}/proactive/stats`, { headers })
      ]);
      setAlerts(alertsRes.data.alerts || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAlerts = async () => {
    setGenerating(true);
    try {
      const res = await axios.get(`${API}/proactive/alerts/generate`, { headers });
      setGeneratedAlerts(res.data);
      toast.success(`Generated ${res.data.summary?.total_alerts || 0} alerts`);
      loadData(); // Refresh stored alerts
    } catch (error) {
      toast.error("Failed to generate alerts");
    } finally {
      setGenerating(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await axios.put(`${API}/proactive/alerts/${alertId}/acknowledge`, {}, { headers });
      toast.success("Alert acknowledged");
      loadData();
    } catch (error) {
      toast.error("Failed to acknowledge alert");
    }
  };

  const handleResolve = async (alertId, notes = "") => {
    try {
      await axios.put(`${API}/proactive/alerts/${alertId}/resolve`, { notes }, { headers });
      toast.success("Alert resolved");
      setSelectedAlert(null);
      loadData();
    } catch (error) {
      toast.error("Failed to resolve alert");
    }
  };

  const handleGenerateDraft = async (alert) => {
    setDraftDialog({ open: true, alert, draft: "" });
    setDraftLoading(true);
    
    try {
      const res = await axios.post(`${API}/proactive/draft-communication`, {
        alert_type: alert.alert_type,
        title: alert.title,
        description: alert.description,
        details: alert.details,
        suggested_action: alert.suggested_action
      }, { headers });
      
      setDraftDialog(prev => ({ ...prev, draft: res.data.draft }));
    } catch (error) {
      toast.error("Failed to generate draft");
      setDraftDialog(prev => ({ ...prev, draft: "[Failed to generate draft]" }));
    } finally {
      setDraftLoading(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.low;
    return (
      <Badge className={`${config.color} text-white`}>
        {config.label}
      </Badge>
    );
  };

  const getAlertIcon = (alertType) => {
    const Icon = ALERT_TYPE_ICONS[alertType] || Zap;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <AdminSidebar>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminSidebar>
    );
  }

  const newAlerts = alerts.filter(a => a.status === "new");
  const acknowledgedAlerts = alerts.filter(a => a.status === "acknowledged");

  return (
    <AdminSidebar>
      <div className="min-h-screen bg-background" data-testid="proactive-agent-page">
        {/* Header */}
        <header className="border-b bg-card sticky top-0 z-10">
          <div className="px-4 md:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="flex items-center gap-2">
                  <Zap className="w-6 h-6 text-primary" />
                  <div>
                    <h1 className="font-serif text-xl font-semibold">Proactive AI Agent</h1>
                    <p className="text-sm text-muted-foreground">Pattern detection & automated alerts</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={loadData}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
                <Button onClick={handleGenerateAlerts} disabled={generating}>
                  {generating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Scan for Patterns
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-8 py-6">
          {/* Stats Overview */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-500/10">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.alerts?.new || 0}</p>
                      <p className="text-sm text-muted-foreground">New Alerts</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-yellow-500/10">
                      <Eye className="w-5 h-5 text-yellow-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.alerts?.acknowledged || 0}</p>
                      <p className="text-sm text-muted-foreground">Acknowledged</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/10">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.alerts?.resolved || 0}</p>
                      <p className="text-sm text-muted-foreground">Resolved</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Bell className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.alerts?.total || 0}</p>
                      <p className="text-sm text-muted-foreground">Total Alerts</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Generated Alerts Preview */}
          {generatedAlerts && (
            <Alert className="mb-6 border-primary/20 bg-primary/5">
              <Sparkles className="w-4 h-4" />
              <AlertDescription>
                <strong>Pattern Scan Complete:</strong> Found {generatedAlerts.summary?.total_alerts} potential alerts
                ({generatedAlerts.summary?.by_priority?.critical || 0} critical,{" "}
                {generatedAlerts.summary?.by_priority?.high || 0} high,{" "}
                {generatedAlerts.summary?.by_priority?.medium || 0} medium,{" "}
                {generatedAlerts.summary?.by_priority?.low || 0} low).
                {generatedAlerts.alerts_stored > 0 && ` ${generatedAlerts.alerts_stored} high-priority alerts saved.`}
              </AlertDescription>
            </Alert>
          )}

          {/* Alerts Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="active">
                Active ({newAlerts.length + acknowledgedAlerts.length})
              </TabsTrigger>
              <TabsTrigger value="new">
                New ({newAlerts.length})
              </TabsTrigger>
              <TabsTrigger value="acknowledged">
                In Progress ({acknowledgedAlerts.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="mt-6">
              <AlertsList 
                alerts={[...newAlerts, ...acknowledgedAlerts]}
                onAcknowledge={handleAcknowledge}
                onResolve={handleResolve}
                onGenerateDraft={handleGenerateDraft}
                getPriorityBadge={getPriorityBadge}
                getAlertIcon={getAlertIcon}
              />
            </TabsContent>

            <TabsContent value="new" className="mt-6">
              <AlertsList 
                alerts={newAlerts}
                onAcknowledge={handleAcknowledge}
                onResolve={handleResolve}
                onGenerateDraft={handleGenerateDraft}
                getPriorityBadge={getPriorityBadge}
                getAlertIcon={getAlertIcon}
              />
            </TabsContent>

            <TabsContent value="acknowledged" className="mt-6">
              <AlertsList 
                alerts={acknowledgedAlerts}
                onAcknowledge={handleAcknowledge}
                onResolve={handleResolve}
                onGenerateDraft={handleGenerateDraft}
                getPriorityBadge={getPriorityBadge}
                getAlertIcon={getAlertIcon}
                showAcknowledge={false}
              />
            </TabsContent>
          </Tabs>
        </main>

        {/* Draft Communication Dialog */}
        <Dialog open={draftDialog.open} onOpenChange={(open) => setDraftDialog({ open, alert: null, draft: "" })}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Draft Community Communication</DialogTitle>
              <DialogDescription>
                AI-generated draft based on alert: {draftDialog.alert?.title}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {draftLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <span className="ml-2">Generating draft...</span>
                </div>
              ) : (
                <Textarea
                  value={draftDialog.draft}
                  onChange={(e) => setDraftDialog(prev => ({ ...prev, draft: e.target.value }))}
                  className="min-h-[300px]"
                  placeholder="Draft will appear here..."
                />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDraftDialog({ open: false, alert: null, draft: "" })}>
                Close
              </Button>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(draftDialog.draft);
                  toast.success("Copied to clipboard!");
                }}
                disabled={!draftDialog.draft}
              >
                Copy to Clipboard
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminSidebar>
  );
};

// Alerts List Component
const AlertsList = ({ alerts, onAcknowledge, onResolve, onGenerateDraft, getPriorityBadge, getAlertIcon, showAcknowledge = true }) => {
  if (alerts.length === 0) {
    return (
      <Alert>
        <CheckCircle className="w-4 h-4" />
        <AlertDescription>
          No alerts in this category. Click "Scan for Patterns" to detect new issues.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <ScrollArea className="h-[500px]">
      <div className="space-y-4">
        {alerts.map((alert) => (
          <Card key={alert.alert_id} className={`border-l-4 ${
            alert.priority === "critical" ? "border-l-red-500" :
            alert.priority === "high" ? "border-l-orange-500" :
            alert.priority === "medium" ? "border-l-yellow-500" : "border-l-blue-500"
          }`}>
            <CardContent className="pt-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    alert.priority === "critical" ? "bg-red-500/10 text-red-500" :
                    alert.priority === "high" ? "bg-orange-500/10 text-orange-500" :
                    alert.priority === "medium" ? "bg-yellow-500/10 text-yellow-500" : "bg-blue-500/10 text-blue-500"
                  }`}>
                    {getAlertIcon(alert.alert_type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold">{alert.title}</h4>
                      {getPriorityBadge(alert.priority)}
                      {alert.status === "acknowledged" && (
                        <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                          In Progress
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{alert.description}</p>
                    
                    {alert.suggested_action && (
                      <p className="text-sm text-primary font-medium mb-2">
                        💡 {alert.suggested_action}
                      </p>
                    )}
                    
                    <p className="text-xs text-muted-foreground">
                      {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 mt-4 pt-3 border-t">
                {showAcknowledge && alert.status === "new" && (
                  <Button size="sm" variant="outline" onClick={() => onAcknowledge(alert.alert_id)}>
                    <Eye className="w-3 h-3 mr-1" />
                    Acknowledge
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => onResolve(alert.alert_id)}>
                  <Check className="w-3 h-3 mr-1" />
                  Resolve
                </Button>
                <Button size="sm" variant="secondary" onClick={() => onGenerateDraft(alert)}>
                  <FileText className="w-3 h-3 mr-1" />
                  Draft Communication
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </ScrollArea>
  );
};

export default ProactiveAgentPage;
